import { trackCalculatorComplete } from "@/components/analytics/conversions";
import { CalculatorArticle } from "@/components/calculators/calculator-article";
import { calculatorArticleHead } from "@/components/calculators/calculator-article-head";
import { oneRmGuide } from "@/components/calculators/guides/one-rm-guide";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { seo } from "@/utils/seo";
import { createFileRoute } from "@tanstack/react-router";
import { Dumbbell, Info, Target } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/_app/essay/1rm-calculator")({
  component: OneRMCalculator,
  head: () => ({
    meta: seo({
      title: "1RM Hesaplayıcı ve E1RM Rehberi | AtalayTheCoach",
      description:
        "Çalışma setinden Epley, Brzycki ve diğer denklemlerle tahmini 1RM hesapla; sonucu yüzde ve RPE tabanlı antrenmanda doğru kullan.",
      keywords:
        "1rm hesaplama, tek tekrar maksimum, powerlifting hesaplayıcı, squat 1rm, bench press 1rm, deadlift 1rm, güç sporları, Alican Atalay",
      canonical: "https://atalaythecoach.com/essay/1rm-calculator",
    }),
    ...calculatorArticleHead({
      canonical: "https://atalaythecoach.com/essay/1rm-calculator",
      headline: "1RM Hesaplayıcı: E1RM Sonucunu Antrenmanda Kullanma Rehberi",
      description:
        "Tahmini 1RM denklemleri, doğru set seçimi ve yüzde tabanlı programlama için uygulamalı rehber.",
    }),
  }),
});

// 1RM Formülleri
const formulas = {
  epley: (weight: number, reps: number) => weight * (1 + reps / 30),
  brzycki: (weight: number, reps: number) => weight / (1.0278 - 0.0278 * reps),
  lander: (weight: number, reps: number) =>
    (100 * weight) / (101.3 - 2.67123 * reps),
  lombardi: (weight: number, reps: number) => weight * reps ** 0.1,
  mayhew: (weight: number, reps: number) =>
    (100 * weight) / (52.2 + 41.9 * Math.exp(-0.055 * reps)),
};

function OneRMCalculator() {
  const [weight, setWeight] = useState<string>("");
  const [reps, setReps] = useState<string>("");
  const [exercise, setExercise] = useState<string>("");
  const [results, setResults] = useState<Record<string, number> | null>(null);

  const calculateOneRM = () => {
    const w = Number.parseFloat(weight);
    const r = Number.parseInt(reps);

    if (!w || !r || r < 1 || r > 20) return;

    const calculated = {
      epley: formulas.epley(w, r),
      brzycki: formulas.brzycki(w, r),
      lander: formulas.lander(w, r),
      lombardi: formulas.lombardi(w, r),
      mayhew: formulas.mayhew(w, r),
    };

    setResults(calculated);
    trackCalculatorComplete("1rm");
  };

  const getAverage = () => {
    if (!results) return 0;
    const values = Object.values(results);
    return values.reduce((a, b) => a + b, 0) / values.length;
  };

  return (
    <div className="min-h-screen bg-background pt-32 pb-16">
      <div className="container mx-auto max-w-4xl px-4">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-primary/10 p-4 dark:bg-primary/20">
              <Dumbbell className="h-12 w-12 text-primary" />
            </div>
          </div>
          <h1 className="mb-6 font-bold text-4xl text-foreground leading-tight md:text-5xl lg:text-6xl">
            1RM Hesaplayıcı
          </h1>
          <p className="mx-auto max-w-3xl text-lg text-muted-foreground leading-relaxed md:text-xl">
            Tek tekrar maksimum (1RM) hesaplayıcı ile powerlifting
            performansınızı ölçün. Squat, bench press ve deadlift için güvenilir
            hesaplama.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-2">
          {/* Hesaplayıcı */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Dumbbell className="h-5 w-5 text-primary" />
                1RM Hesapla
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="exercise">Egzersiz Seçin</Label>
                <Select value={exercise} onValueChange={setExercise}>
                  <SelectTrigger>
                    <SelectValue placeholder="Egzersiz seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="squat">Squat</SelectItem>
                    <SelectItem value="bench">Bench Press</SelectItem>
                    <SelectItem value="deadlift">Deadlift</SelectItem>
                    <SelectItem value="overhead">Overhead Press</SelectItem>
                    <SelectItem value="other">Diğer</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="weight">Ağırlık (kg)</Label>
                <Input
                  id="weight"
                  type="number"
                  placeholder="100"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reps">Tekrar Sayısı</Label>
                <Input
                  id="reps"
                  type="number"
                  placeholder="5"
                  value={reps}
                  onChange={(e) => setReps(e.target.value)}
                  min="1"
                  max="20"
                />
              </div>

              <Button
                onClick={calculateOneRM}
                className="w-full"
                disabled={!weight || !reps}
              >
                1RM Hesapla
              </Button>
            </CardContent>
          </Card>

          {/* Sonuçlar */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                Sonuçlar
              </CardTitle>
            </CardHeader>
            <CardContent>
              {results ? (
                <div className="space-y-4">
                  <div className="rounded-lg bg-primary/10 p-4">
                    <div className="text-center">
                      <div className="font-bold text-2xl text-primary">
                        {getAverage().toFixed(1)} kg
                      </div>
                      <div className="text-muted-foreground text-sm">
                        Ortalama 1RM
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-semibold">Formül Sonuçları:</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        Epley:{" "}
                        <span className="font-mono">
                          {results.epley.toFixed(1)} kg
                        </span>
                      </div>
                      <div>
                        Brzycki:{" "}
                        <span className="font-mono">
                          {results.brzycki.toFixed(1)} kg
                        </span>
                      </div>
                      <div>
                        Lander:{" "}
                        <span className="font-mono">
                          {results.lander.toFixed(1)} kg
                        </span>
                      </div>
                      <div>
                        Lombardi:{" "}
                        <span className="font-mono">
                          {results.lombardi.toFixed(1)} kg
                        </span>
                      </div>
                      <div>
                        Mayhew:{" "}
                        <span className="font-mono">
                          {results.mayhew.toFixed(1)} kg
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-muted bg-muted/30 p-4">
                    <div className="flex items-start gap-3">
                      <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-muted-foreground" />
                      <p className="text-muted-foreground text-sm">
                        <strong className="text-foreground">
                          Güvenlik Uyarısı:
                        </strong>{" "}
                        1RM testleri riskli olabilir. Daima spotçı eşliğinde ve
                        uygun ısınma sonrası yapın.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  Hesaplama yapmak için sol taraftaki formu doldurun
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <CalculatorArticle content={oneRmGuide} />
      </div>
    </div>
  );
}
